import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { registerSchema } from '../../lib/validators/authSchemas';
import { useRegisterMutation } from '../../features/auth/authApi';
import { authErrorMessage } from '../../lib/authErrorMessage';
import Input from '../../components/ui/Input';
import PasswordInput from '../../components/ui/PasswordInput';
import PhoneInput from '../../components/ui/PhoneInput';
import Button from '../../components/ui/Button';

export default function Register() {
  const navigate = useNavigate();
  const [registerUser, { isLoading }] = useRegisterMutation();

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(registerSchema), defaultValues: { phone: '' } });

  const onSubmit = async ({ password_confirmation, ...values }) => {
    try {
      await registerUser(values).unwrap();
      navigate('/verify-email', { state: { email: values.email } });
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
      } else {
        setError('root', { message: authErrorMessage(err, { fallback: 'Registration failed.' }) });
      }
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Create your account</h1>
      <p className="text-sm text-foreground-muted mb-6">
        Start tracking your fixed-return investments with Cavan Capital.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" error={errors.first_name?.message} {...register('first_name')} />
          <Input label="Last name" error={errors.last_name?.message} {...register('last_name')} />
        </div>
        <Input label="Email address" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInput label="Phone number" name="phone" value={field.value} onChange={field.onChange} error={errors.phone?.message} />
          )}
        />
        <PasswordInput
          label="Password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordInput
          label="Confirm password"
          autoComplete="new-password"
          error={errors.password_confirmation?.message}
          {...register('password_confirmation')}
        />

        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Create account
        </Button>
      </form>

      <p className="text-sm text-foreground-muted mt-6 text-center">
        Already have an account?{' '}
        <Link to="/login" className="text-accent hover:underline">
          Log in
        </Link>
      </p>
    </div>
  );
}
